import { Box } from '@wix/design-system';
import classnames from 'classnames';
import debounce from 'lodash/debounce';
import React, { createRef, FC, RefObject, useCallback, useEffect, useMemo, useRef } from 'react';
import { useSelector } from 'react-redux';

import { assertUnreachable, getLast } from '../../../../util/guards';
import { ScrollBox } from '../../../common/components/ScrollBox';
import { ChatScrollToPositions } from '../../../common/reducers/types';
import { selectItems, selectScrollTo } from '../../../common/selectors/chat';
import { selectWidgetMobileView } from '../../../common/selectors/widget';
import { StatusBanner } from '../../../components/header/StatusBanner';
import { renderModel } from '../../../util/renderModel';
import { useOfflineListener } from '../../hooks/useOfflineListener';
import { AiChatbotWelcomeScreen } from '../AiChatbotWelcomeScreen';
import { LegalDisclaimer } from '../LegalDisclaimer';
import { UserGreeting } from '../UserGreeting';
import s from './Chat.scss';
import { ChatMessage } from './ChatMessage';
import { usePolling } from './usePolling';
import {
  getMessageIdToScroll,
  isElementScrollable,
  isElementScrolledToBottom,
  isFirstByAuthor,
  isFirstOfType,
  setA11yFocus,
} from './utils';

const isFullWidth = renderModel.isFullWidth();
const isWidget = renderModel.isWidget();
const isCopilot = renderModel.isCopilot();
const WIDGET_RESIZE_DEBOUNCE_TIME = 50; // ms

export const Chat: FC = () => {
  const items = useSelector(selectItems);
  const scrollTo = useSelector(selectScrollTo);
  const widgetMobileView = useSelector(selectWidgetMobileView);
  const isScrolledToBottomRef = useRef(false);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const handleScrollTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  usePolling();
  useOfflineListener();

  const scrollItemsRefs: {
    [key: string]: RefObject<HTMLDivElement>;
  } = useMemo(
    () =>
      items.reduce((acc, item) => {
        acc[item.id] = createRef<HTMLDivElement>();
        return acc;
      }, {}),
    [items],
  );

  const scrollToBottomRef = useRef(() => {
    const containerElement = scrollContainerRef.current;
    const canScroll = containerElement && isElementScrollable(containerElement);

    if (!canScroll) {
      return;
    }

    containerElement.scroll({
      top: containerElement.scrollHeight,
      behavior: 'smooth',
    });
  });

  const checkIfScrolledToBottomRef = useRef(() => {
    if (scrollContainerRef.current) {
      const scrollToBottomTolerance = 24; // setting tolerance equal to bottom padding
      isScrolledToBottomRef.current =
        isElementScrollable(scrollContainerRef.current) &&
        isElementScrolledToBottom(scrollContainerRef.current, scrollToBottomTolerance);
    }
  });

  const scrollToFn = useCallback((element: HTMLDivElement | null) => {
    const container = scrollContainerRef.current;
    const canScroll = element && container && isElementScrollable(container);

    if (!canScroll) {
      return;
    }

    const offsetTop = element.offsetTop;
    const scrollGap = -12; // SP2 - distance between messages

    container.scroll({
      top: offsetTop + scrollGap,
      behavior: 'smooth',
    });
  }, []);

  const scrollToItemId = useCallback(
    (id: string) => {
      const itemElement = scrollItemsRefs[id]?.current;
      scrollToFn(itemElement);
    },
    [scrollItemsRefs, scrollToFn],
  );

  const setLastItemFocus = useCallback(() => {
    // use setA11yFocus only when document in focus and chatbot is expanded
    if (document.hasFocus()) {
      setA11yFocus(items, getLast(items));
    }
  }, [items]);

  const scrollToLastItem = useCallback(() => {
    const messageIdToScroll = getMessageIdToScroll(items, getLast(items));
    if (!messageIdToScroll) {
      return;
    }

    // Update if scrolled to bottom flag before start scrolling
    checkIfScrolledToBottomRef.current();

    if (messageIdToScroll === items[0]?.id) {
      // Scrolling to the top of container for the first item.
      // Important if status banner is shown as we should keep it visible.
      scrollContainerRef.current?.scroll({ top: 0 });
    } else {
      scrollToItemId(messageIdToScroll);
    }
  }, [items, scrollToItemId]);

  useEffect(() => {
    if (!scrollTo) {
      return;
    }

    const { position } = scrollTo;

    switch (position) {
      case ChatScrollToPositions.Bottom:
        return scrollToBottomRef.current();
      default:
        assertUnreachable(position);
    }
  }, [scrollTo]);

  const handleScroll = useCallback(() => {
    // Calculating if scrolled to bottom should be postponed till next tick so it is called after resize observer finished fixing scroll in the effect below
    handleScrollTimeoutRef.current = setTimeout(() => checkIfScrolledToBottomRef.current());
  }, []);

  useEffect(() => {
    if (handleScrollTimeoutRef.current) {
      clearTimeout(handleScrollTimeoutRef.current);
    }
  }, []);

  useEffect(() => {
    setLastItemFocus();
    scrollToLastItem();
  }, [items, setLastItemFocus, scrollToLastItem]);

  useEffect(() => {
    const onWidgetSizeChanged = debounce(scrollToLastItem, WIDGET_RESIZE_DEBOUNCE_TIME);

    function onScrollContainerResize() {
      if (!scrollContainerRef.current) {
        return;
      }

      // restore bottom scroll position after resize(if it was scrolled to bottom before resize event)
      if (isScrolledToBottomRef.current) {
        scrollContainerRef.current.scroll({
          top: scrollContainerRef.current.scrollHeight,
        });
        return;
      }

      // if widget size is changed, it was maximized/minimized(the only possible case is navigating from maximized article to chatbot)
      if (isWidget && !widgetMobileView) {
        onWidgetSizeChanged();
      }
    }

    onScrollContainerResize();

    if (scrollContainerRef.current) {
      const element = scrollContainerRef.current;
      const resizeObserver = new ResizeObserver(onScrollContainerResize);
      resizeObserver.observe(element);

      return () => {
        if (element) {
          resizeObserver.unobserve(element);
        }
      };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scrollContainerRef.current]);

  const onItemLoad = useCallback(() => {
    // Re-adjusting scroll if item called onLoad callback
    scrollToLastItem();
  }, [scrollToLastItem]);

  const messagesPresent = items.length > 0;

  return (
    <ScrollBox
      ref={scrollContainerRef}
      onScroll={handleScroll}
      overflowY="scroll"
      flexGrow={1}
      direction="vertical"
    >
      {!isFullWidth && <StatusBanner autoHide={true} />}
      <Box
        direction="vertical"
        width="100%"
        flexGrow={1}
        className={classnames(s.scrollContainer, { [s.copilotScrollContainer]: isCopilot })}
      >
        <Box
          width="720px"
          maxWidth="100%"
          margin="0 auto"
          padding="SP2 0"
          paddingBottom="11"
          direction="vertical"
        >
          <Box direction="vertical">
            <UserGreeting>
              <AiChatbotWelcomeScreen />
            </UserGreeting>
          </Box>

          {messagesPresent && (
            <>
              {!isCopilot && (
                <Box direction="vertical" className={s.fadeIn}>
                  <LegalDisclaimer />
                </Box>
              )}
              <Box direction="vertical" gap="SP1">
                {items.map((item, i) => {
                  const isFirstInGroup = isFirstByAuthor(items, i);
                  const withExtraMargin = isFirstInGroup || isFirstOfType(items, i);
                  return (
                    <Box key={item.id} marginTop={withExtraMargin ? 'SP1' : undefined} direction="vertical">
                      <ChatMessage
                        ref={scrollItemsRefs[item.id]}
                        item={item}
                        isFirstInGroup={isFirstInGroup}
                        onLoad={onItemLoad}
                        scrollToFn={scrollToFn}
                      />
                    </Box>
                  );
                })}
              </Box>
            </>
          )}
        </Box>
      </Box>
    </ScrollBox>
  );
};
